import axios from '../plugins/axios'
import { Module } from 'vuex'

export interface Team {
    username: string
    token: string
}

const teamModule: Module<any, any> = {
    state: {
        workspace: {},
    },
    actions: {
        async initWorkspaceIndex() {
            if (this.workspaceIndexLoading) {
        		await this.initWorkspacePromise
        		return
        	}
            this.workspaceIndexLoading = true
            this.initWorkspacePromise = this.dispatch('initIndex', {
                id: 'workspace',
                idField: 'id',
                indexedFields: [
                    'name',
                    'subdomain',
                    'uuid',
                    'workspaceType',
                    'logo',
                ],
                source: '/api/workspaces',
                store: [
                    '@id',
                    '@type',
                    'id',
                    'uuid',
                    'name',
                    'subdomain',
                    'workspaceType',
                    'logo',
                    'website',
                    'country',
                    'customerId',
                    'timezone',
                    'isVersion2',
                ]
            })
            await this.initWorkspacePromise
            this.workspaceIndexLoading = false
            this.initWorkspacePromise = null
        },

        async getWorkspace(_, id) {
            if (id === undefined) {
                throw new Error('Invalid workspace ID specified')
            }

            const indexExists = await this.dispatch('indexExists', 'workspace')

            if (indexExists) {
                return await this.dispatch('getRecord', {
                    index: 'workspace',
                    id,
                })
            } else {
                return await this.dispatch('getWorkspaceFresh', id)
            }
        },

        async getWorkspaceFresh(_, id) {
            const indexExists = await this.dispatch('indexExists', 'workspace')

            const { data } = await axios.get('/api/workspaces' + (id ? '/' + id : ''))

            // Keep the local workspace list current when it's already loaded. Loading one workspace
            // doesn't load the whole list (up to 2,000 workspaces); that happens when a list or
            // search needs it (getWorkspaces).
            if (data && indexExists) {
                await this.dispatch('addRecord', {
                    index: 'workspace',
                    record: data,
                })
            }

            return data
        },

        async updateWorkspace(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}`,
                params,
            )

            return data
        },
        
        async updateWorkspaceName(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}/name`,
                params,
            )

            return data
        },

        async updateWorkspaceDetails(_, params) {
            const { data } = await axios.put(
                `/api/workspaces/${params.id}/details`,
                params,
            )

            return data
        },

        async deleteWorkspace(_, id ) {
            const { data } = await axios.delete(`/api/workspaces/${id}`)
            return data
        },

        // How many workspaces the user can see, without downloading them all.
        async getWorkspacesCount() {
            const { data } = await axios.get('/api/workspaces?page=1&itemPerPage=1')
            return data?.['hydra:totalItems'] ?? data?.['hydra:member']?.length ?? 0
        },

        async getWorkspaces(_, params) {
            const indexExists = await this.dispatch('indexExists', 'workspace')
            if (!indexExists || params?.refresh) {
                await this.dispatch('initWorkspaceIndex')
            }

            const sortNameAsc = (items) => {
                return items?.sort((a, b) =>
                    a.name?.toLowerCase() < b.name?.toLowerCase()
                        ? -1
                        : a.name?.toLowerCase() > b.name?.toLowerCase()
                        ? 1
                        : 0,
                )
            }

            const getSortOrder = () => {
                return sortNameAsc
            }

            let records = null
            records = await this.dispatch('getRecords', {
                index: 'workspace',
                term: params?.search,
                fields: ['name'],
                page: params?.page,
                perPage: params?.perPage,
                filters: [],
                sort: getSortOrder(),
            })

            return records
        },

        async getWorkspaceIntegration(_, params) {
            const page = 'page=' + (params?.page ?? 1)
            const perPage = params?.perPage
                ? '&itemPerPage=' + params.perPage
                : ''

            const { data } = await axios.get(
                '/api/workspaces_integration?' + 
                page + 
                perPage,
            )

            return data
        },

        async updateWorkspaceIntegration(_, params) {
            const { data } = await axios.put(
                `/api/workspaces_integration/${params.id}`, params,
            )

            return data
        },
    },
}

export default teamModule
